/**
 * HeaderSection
 * 
 * Renders the header section of control card including:
 * - Company logo and certification logo
 * - Title and subtitle
 * - Company information table (report number, owner)
 */

import { jsPDF } from 'jspdf'
import { CONTROL_CARD_CONFIG, CONTROL_CARD_TEXT } from '../../config'
import { PDFRenderer, TableRenderer, ImageRenderer, type ControlCardData } from '../../infrastructure'

/**
 * Logo configuration
 */
export interface LogoConfig {
  company?: string
  certification?: string
}

/**
 * Page header configuration for continuation pages
 */
export interface PageHeaderData {
  reportNumber: string
  ownerName: string
  serviceOrder?: string | null
  logos?: LogoConfig
}

/**
 * Header section renderer
 */
export class HeaderSection {
  private readonly pdfRenderer: PDFRenderer
  private readonly tableRenderer: TableRenderer
  private readonly imageRenderer: ImageRenderer
  private readonly config: typeof CONTROL_CARD_CONFIG

  constructor(config: typeof CONTROL_CARD_CONFIG = CONTROL_CARD_CONFIG) {
    this.config = config
    this.pdfRenderer = new PDFRenderer(config)
    this.tableRenderer = new TableRenderer(config)
    this.imageRenderer = new ImageRenderer(config)
  }

  /**
   * Render complete header section
   * Returns the Y position after rendering
   */
  async render(
    doc: jsPDF,
    data: ControlCardData,
    reportNumber: string,
    logos?: LogoConfig
  ): Promise<number> {

    // Render text FIRST (matches original template behavior)
    this.renderCompanyHeader(doc)

    this.renderTitle(doc)

    // Then add logos (with transparent background, they won't cover the text)
    if (logos?.company) {
        await this.imageRenderer.addLogo(
        doc,
        logos.company,
        this.config.logos.company
      )
    }

    if (logos?.certification) {
        await this.imageRenderer.addLogo(
        doc,
        logos.certification,
        this.config.logos.certification
      )
    }

    // Render company information table
    const finalY = this.tableRenderer.renderCompanyInfoTable(
      doc,
      reportNumber,
      data.mainEquipment.ownerName || 'UNKNOWN OWNER',
      data.mainEquipment.serviceOrder,
      this.config.layout.margin.top
    )

    return finalY
  }

  /**
   * Render header on continuation pages (when content spans multiple pages)
   * Includes logos, title, subtitle, and company info table
   * Returns the Y position after rendering
   */
  async renderContinuationHeader(
    doc: jsPDF,
    headerData: PageHeaderData
  ): Promise<number> {
    // Render text elements
    this.renderCompanyHeader(doc)
    this.renderTitle(doc)

    // Add logos if provided
    if (headerData.logos?.company) {
      await this.imageRenderer.addLogo(
        doc,
        headerData.logos.company,
        this.config.logos.company
      )
    }

    if (headerData.logos?.certification) {
      await this.imageRenderer.addLogo(
        doc,
        headerData.logos.certification,
        this.config.logos.certification
      )
    }

    // Render company information table
    const finalY = this.tableRenderer.renderCompanyInfoTable(
      doc,
      headerData.reportNumber,
      headerData.ownerName,
      headerData.serviceOrder,
      this.config.layout.margin.top
    )

    return finalY
  }

  /**
   * Create a callback function for rendering continuation headers
   * This can be passed to TableRenderer for use during pagination
   */
  createContinuationHeaderCallback(
    headerData: PageHeaderData
  ): (doc: jsPDF) => Promise<number> {
    return async (doc: jsPDF) => {
      return this.renderContinuationHeader(doc, headerData)
    }
  }

  /**
   * Render company header (STM)
   */
  private renderCompanyHeader(doc: jsPDF): void {
    // Render directly to the passed doc instance
    doc.setFont(this.config.typography.companyHeader.font, this.config.typography.companyHeader.weight)
    doc.setFontSize(this.config.typography.companyHeader.size)
    doc.text('STM', 20, 20)
  }

  /**
   * Render main title and subtitle
   */
  private renderTitle(doc: jsPDF): void {
    const pageWidth = doc.internal.pageSize.getWidth()
    const centerX = pageWidth / 2

    // Main title (matches original template position)
    doc.setFont(this.config.typography.title.font, this.config.typography.title.weight)
    doc.setFontSize(this.config.typography.title.size)
    doc.setTextColor(...this.config.colors.text)
    doc.text(CONTROL_CARD_TEXT.no.title, centerX, 15, { align: 'center' })

    // Subtitle (matches original template position)
    doc.setFont(this.config.typography.subtitle.font, this.config.typography.subtitle.weight)
    doc.setFontSize(this.config.typography.subtitle.size)
    doc.setTextColor(...this.config.colors.text)
    doc.text(CONTROL_CARD_TEXT.no.subtitle, centerX, 22, { align: 'center' })
  }
}

